import { BehaviorSubject, Observable } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import {
  IAddress,
  IContact,
  ContactTypes,
  IContactClosureStatus,
  IContactClosureHistory,
  IContactCallLogsPagination,
  IContactCallLogsFilter,
  IContactActivitiesPagination,
  IContactActivitiesFilter,
  ICreateContactActivityPayload,
  IContactActivity,
  IContactReminder,
  IReminderSummary,
  IContactRemindersPagination,
  IContactRemindersFilter,
  ICreateReminderPayload,
  IReminderActionPayload
} from '@models/contacts/hrm/contacts';
import { HTTPResponse, HTTPSearchResponse, APP_CONFIG } from '@mavles/shared';

// TODO: Replace with generic static data service if available in shared, or inject appropriately.
// Temporary workaround for static data service dependency
@Injectable({
  providedIn: 'root'
})
export class ContactApiService {
  protected httpClient = inject(HttpClient);
  private readonly appConfig = inject(APP_CONFIG);

  protected readonly contactAPI = `${this.appConfig.apiUrl}hrm/app-contact`;
  protected readonly hrmAPI = `${this.appConfig.apiUrl}hrm`;
  protected readonly contactReportAPI = `${this.appConfig.apiUrl}report/`;
  
  public contacts$ = new BehaviorSubject<IContact[]>([]);
  contacts: IContact[] = [];

  constructor() {
    this.refresh();
  }

  search(params: Record<string, any>): Observable<HTTPSearchResponse<IContact[]>> {
    return this.httpClient.get<HTTPSearchResponse<IContact[]>>(`${this.contactAPI}/search`, { params });
  }

  searchContactCd(params: Record<string, any>, contactTypeCd: string): Observable<HTTPSearchResponse<IContact[]>> {
    return this.search({ ...params, contact_type_code: contactTypeCd });
  }

  get(id: number): Observable<HTTPResponse<IContact>> {
    return this.httpClient.get<HTTPResponse<IContact>>(`${this.contactAPI}/get/${id}`);
  }

  getAll(): Observable<HTTPResponse<IContact[]>> {
    return this.httpClient.get<HTTPResponse<IContact[]>>(this.contactAPI);
  }

  getAllByType(contactType: ContactTypes): Observable<HTTPResponse<IContact[]>> {
    return this.httpClient.get<HTTPResponse<IContact[]>>(`${this.contactAPI}/getall/${contactType}`);
  }

  save(contact: IContact): Observable<HTTPResponse<IContact>> {
    const action = contact.contact_id ? 'update' : 'add';
    return this.httpClient.post<HTTPResponse<IContact>>(`${this.contactAPI}/${action}`, contact);
  }

  getAddressByContactID(contactId: number): Observable<HTTPResponse<IAddress[]>> {
    return this.httpClient.get<HTTPResponse<IAddress[]>>(`${this.contactAPI}/address/get-address/${contactId}`);
  }

  public readonly RECENT_STORAGE_KEY = 'MAVLES_RECENT_CONTACT_IDS';

  recordRecentContact(contactId: number): void {
    if (!contactId) return;
    try {
      const data = localStorage.getItem(this.RECENT_STORAGE_KEY);
      const existing: number[] = data ? JSON.parse(data) : [];
      const updated = [Number(contactId), ...existing.filter(i => i !== Number(contactId))].slice(0, 15);
      localStorage.setItem(this.RECENT_STORAGE_KEY, JSON.stringify(updated));
    } catch {
      // Storage unavailable or quota exceeded
    }
  }

  getRecentContactIds(): number[] {
    try {
      const data = localStorage.getItem(this.RECENT_STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  changes$(): Observable<IContact[]> {
    return this.contacts$.asObservable();
  }

  getAllByContactType(types: number[] | undefined): IContact[] {
    if (!types || !Array.isArray(types)) return [];
    return this.contacts.filter(c => types.includes(c.contact_type_id));
  }
 
  refresh(): void {
    this.getAll().subscribe(c => {
      this.contacts = c.data || [];
      this.contacts$.next(this.contacts);
    });
  }

  get isContactAvailable(): boolean {
    return this.contacts.length > 0;
  }

  downloadReport(contact: IContact): Observable<Blob> {
    const headers = new HttpHeaders().set('Accept', 'application/pdf');
    const url = `${this.contactReportAPI}/${contact.contact_type?.contact_type_code}/download/pdf/${contact.contact_id}`;
    return this.httpClient.get<Blob>(url, { headers, responseType: 'blob' as 'json' });
  }

  // =========================================================================
  // Contact Account Closure Workflow
  // =========================================================================

  getClosureStatus(contactId: number): Observable<HTTPResponse<IContactClosureStatus>> {
    return this.httpClient.get<HTTPResponse<IContactClosureStatus>>(`${this.hrmAPI}/contacts/${contactId}/closure-status`);
  }

  getClosureHistory(contactId: number): Observable<HTTPResponse<IContactClosureHistory>> {
    return this.httpClient.get<HTTPResponse<IContactClosureHistory>>(`${this.hrmAPI}/contacts/${contactId}/closure-history`);
  }

  submitClosureRequest(contactId: number, comments?: string): Observable<HTTPResponse<any>> {
    const payload = comments !== undefined ? { comments } : {};
    return this.httpClient.post<HTTPResponse<any>>(`${this.hrmAPI}/contacts/${contactId}/closure-requests`, payload);
  }

  approveClosureRequest(requestId: number, comments?: string): Observable<HTTPResponse<any>> {
    const payload = comments !== undefined ? { comments } : {};
    return this.httpClient.post<HTTPResponse<any>>(`${this.hrmAPI}/contact-closure-requests/${requestId}/approve`, payload);
  }

  rejectClosureRequest(requestId: number, comments: string): Observable<HTTPResponse<any>> {
    return this.httpClient.post<HTTPResponse<any>>(`${this.hrmAPI}/contact-closure-requests/${requestId}/reject`, { comments });
  }

  cancelClosureRequest(requestId: number, comments?: string): Observable<HTTPResponse<any>> {
    const payload = comments !== undefined ? { comments } : {};
    return this.httpClient.post<HTTPResponse<any>>(`${this.hrmAPI}/contact-closure-requests/${requestId}/cancel`, payload);
  }

  getPendingClosureRequests(params?: any): Observable<HTTPResponse<any>> {
    const requestParams = { status: 'waiting_for_approval', ...params };
    return this.httpClient.get<HTTPResponse<any>>(`${this.hrmAPI}/contact-closure-requests`, { params: requestParams });
  }

  // =========================================================================
  // Contact Lead-to-Customer Conversion
  // =========================================================================

  convertToCustomer(contactId: number, notes?: string): Observable<HTTPResponse<any>> {
    const payload = notes !== undefined ? { notes } : {};
    return this.httpClient.post<HTTPResponse<any>>(
      `${this.hrmAPI}/contacts/${contactId}/convert-to-customer`,
      payload
    );
  }

  getConversionHistory(contactId: number): Observable<HTTPResponse<any>> {
    return this.httpClient.get<HTTPResponse<any>>(
      `${this.hrmAPI}/contacts/${contactId}/conversion-history`
    );
  }

  // =========================================================================
  // Contact Field Call Logs (Mobile Sync)
  // =========================================================================

  getCallLogs(contactId: number, params?: IContactCallLogsFilter): Observable<HTTPResponse<IContactCallLogsPagination>> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.page !== undefined && params.page !== null) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.page_size !== undefined && params.page_size !== null) {
        httpParams = httpParams.set('page_size', params.page_size.toString());
      }
      if (params.call_type) {
        httpParams = httpParams.set('call_type', params.call_type);
      }
      if (params.from_date) {
        httpParams = httpParams.set('from_date', params.from_date);
      }
      if (params.to_date) {
        httpParams = httpParams.set('to_date', params.to_date);
      }
    }
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.get<HTTPResponse<IContactCallLogsPagination>>(
      `${baseUrl}mobile/contacts/${contactId}/call-logs`,
      { params: httpParams }
    );
  }

  // =========================================================================
  // Contact Activities (Timeline & Logging)
  // =========================================================================

  getActivities(contactId: number, params?: IContactActivitiesFilter): Observable<HTTPResponse<IContactActivitiesPagination>> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.page !== undefined && params.page !== null) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.page_size !== undefined && params.page_size !== null) {
        httpParams = httpParams.set('page_size', params.page_size.toString());
      }
    }
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.get<HTTPResponse<IContactActivitiesPagination>>(
      `${baseUrl}mobile/contacts/${contactId}/activities`,
      { params: httpParams }
    );
  }

  createActivity(contactId: number, payload: ICreateContactActivityPayload): Observable<HTTPResponse<IContactActivity>> {
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.post<HTTPResponse<IContactActivity>>(
      `${baseUrl}mobile/contacts/${contactId}/activities`,
      payload
    );
  }

  // =========================================================================
  // Contact Reminders / Follow-Ups
  // =========================================================================

  getReminderSummary(): Observable<HTTPResponse<IReminderSummary>> {
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.get<HTTPResponse<IReminderSummary>>(
      `${baseUrl}mobile/reminders/summary`
    );
  }

  getReminders(params?: IContactRemindersFilter): Observable<HTTPResponse<IContactRemindersPagination>> {
    let httpParams = new HttpParams();
    if (params) {
      if (params.preset) {
        httpParams = httpParams.set('preset', params.preset);
      }
      if (params.contact_id !== undefined && params.contact_id !== null) {
        httpParams = httpParams.set('contact_id', params.contact_id.toString());
      }
      if (params.assigned_to !== undefined && params.assigned_to !== null) {
        httpParams = httpParams.set('assigned_to', params.assigned_to.toString());
      }
      if (params.reminder_status) {
        httpParams = httpParams.set('reminder_status', params.reminder_status);
      }
      if (params.from_date) {
        httpParams = httpParams.set('from_date', params.from_date);
      }
      if (params.to_date) {
        httpParams = httpParams.set('to_date', params.to_date);
      }
      if (params.page !== undefined && params.page !== null) {
        httpParams = httpParams.set('page', params.page.toString());
      }
      if (params.page_size !== undefined && params.page_size !== null) {
        httpParams = httpParams.set('page_size', params.page_size.toString());
      }
    }
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.get<HTTPResponse<IContactRemindersPagination>>(
      `${baseUrl}mobile/reminders`,
      { params: httpParams }
    );
  }

  getReminder(reminderId: number): Observable<HTTPResponse<IContactReminder>> {
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.get<HTTPResponse<IContactReminder>>(
      `${baseUrl}mobile/reminders/${reminderId}`
    );
  }

  createReminder(payload: ICreateReminderPayload): Observable<HTTPResponse<IContactReminder>> {
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.post<HTTPResponse<IContactReminder>>(
      `${baseUrl}mobile/reminders`,
      payload
    );
  }

  recordReminderAction(reminderId: number, payload: IReminderActionPayload): Observable<HTTPResponse<any>> {
    const baseUrl = this.appConfig.apiUrl.endsWith('/') ? this.appConfig.apiUrl : `${this.appConfig.apiUrl}/`;
    return this.httpClient.post<HTTPResponse<any>>(
      `${baseUrl}mobile/reminders/${reminderId}/actions`,
      payload
    );
  }
}

